import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import PilotPanel from '../components/PilotPanel.jsx';
import api from '../lib/api.js';

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try { const j = await api.getJob(jobId); setJob(j.job); }
      catch (e) { setErr(e.message); }
    })();
  }, [jobId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/dashboard" className="text-sm text-navy">← Back to dashboard</Link>
        {err && <div className="card card-pad mt-6 border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}
        {!job && !err && <div className="card card-pad mt-6 text-sm text-ink-muted">Loading…</div>}
        {job && (
          <div className="mt-6 space-y-6">
            <div className="card card-pad">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-xs uppercase tracking-widest text-ink-muted">Job</div>
                  <h1 className="text-3xl font-bold text-navy">{job.client_name || 'Untitled job'}</h1>
                  <div className="text-sm text-ink-muted mt-1 font-mono">{job.id}</div>
                </div>
                <span className={`tag ${job.status === 'completed' ? 'tag-green' : job.status === 'failed' ? 'tag-red' : 'tag-amber'}`}>{job.status}</span>
              </div>
              <div className="gold-stripe my-4" />
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <KV label="Project type" value={job.project_type} />
                <KV label="Jurisdiction" value={job.jurisdiction} />
                <KV label="Contact" value={job.contact_email} />
                <KV label="Outputs" value={(job.formats || []).join(', ') || '—'} />
                <KV label="Rows" value={job.row_count ?? '—'} />
                <KV label="Geocode hit" value={job.geocode_hit_rate ? `${(job.geocode_hit_rate * 100).toFixed(1)}%` : '—'} />
              </div>
            </div>

            {job.delivery && (job.delivery.files || []).length > 0 && (
              <div className="card card-pad">
                <h2 className="font-semibold text-navy mb-3">Downloads</h2>
                <ul className="text-sm space-y-1">
                  {(job.delivery.files || []).map((f, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 p-2 bg-slate-50 rounded">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs text-navy truncate">{f.name}</div>
                        {f.size !== undefined && (
                          <div className="text-[11px] text-ink-muted">{Math.max(1, Math.round((f.size || 0) / 1024))} KB</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a href={`/api/jobs/${job.id}/download/${encodeURIComponent(f.name)}`}
                           className="btn btn-sm btn-gold"
                           download={f.name}>
                          Download
                        </a>
                        {f.url && (
                          <a href={f.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">
                            Open ↗
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-ink-muted mt-3">
                  Files are generated on this install and stored locally. Click Download to save to your Downloads folder.
                </div>
              </div>
            )}

            {job.sentinel && (
              <div className="card card-pad">
                <h2 className="font-semibold text-navy mb-3">QA result</h2>
                <div className="mb-3">
                  <span className={`tag ${job.sentinel.verdict === 'PASS' ? 'tag-green' : job.sentinel.verdict === 'WARN' ? 'tag-amber' : 'tag-red'}`}>{job.sentinel.verdict}</span>
                </div>
                <ul className="text-sm space-y-1">
                  {(job.sentinel.rules || []).map((r, i) => (
                    <li key={i} className="flex justify-between text-xs">
                      <span>{r.id}: {r.label}</span>
                      <span className={r.status === 'PASS' ? 'text-emerald-600' : r.status === 'WARN' ? 'text-amber-600' : 'text-red-600'}>{r.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Mr Pilot — only mounts for v2 jobs (those with agent_lineage) */}
            {(job.agent_lineage?.length ?? 0) > 0 && (
              <PilotPanel jobId={job.id} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-semibold text-navy">{value || '—'}</div>
    </div>
  );
}
